import React, { FC, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, Navigate, Routes, useNavigate, useLocation, useMatch, useParams } from "react-router-dom";
import { BackArrow, Button, ModalVariables, Tabs, TopProgress } from "../../components";
import MenuButton from "../../components/button/menu-button";
import DropOptions from "../../components/header/components/drop-options";
import { PropsContainer } from "../../components/table/styles";
import { icons } from "../../icons";
import {
  Container,
  HeadWrapper,
  RightHeadContainer,
  InstanceName,
  NavWrapper,
  InstanceHeadInfo,
} from './styles'
import {
  Pools,
  Policies,
  Overview,
} from './components'
import { BodyDefault } from "../../components/typography/styled";
import { loadBalancerService } from "../../api";
import { useUi } from "../../context/ui.context";
import { handleStatusName } from "../../utils/handle-status-name";
import { GreenDot, Greentext, StateWrapper } from "../Instances/styles";
// import CreateLoadBalancer from "../create-page";
import { permissionSurfaces, tabPermissionSurfaces, usePermission, usePermissionActionFilter, usePermissionDecision } from '../../permissions';

const LbListeners:FC = () => {
  const navigate = useNavigate()
  const {lbId, listenerId} = useParams()
  const [t] = useTranslation()
  const [options, setOptions] = useState(false)
  const [listener, setListener] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [subnets, setSubnets] = useState<any>([])
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [alertTitle, setAlertTitle] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [modalType, setModalType] = useState<any>('')
  const {viewAlert} = useUi()
  const { mergeEntityPermissions } = usePermission()
  const canViewOverview = usePermissionDecision(tabPermissionSurfaces.listenerOverview).allowed
  const canViewPools = usePermissionDecision(tabPermissionSurfaces.listenerPools).allowed
  const canViewPolicies = usePermissionDecision(tabPermissionSurfaces.listenerPolicies).allowed
  const canEditListener = usePermissionDecision(permissionSurfaces.detailActions.listenerEdit, { itemAllowedActions: listener?.allowed_actions }).allowed
  const canDeleteListener = usePermissionDecision(permissionSurfaces.detailActions.listenerDelete, { itemAllowedActions: listener?.allowed_actions }).allowed
  const updateLb = () => {
    if(listenerId){
      loadBalancerService.getListenerById(listenerId)
      .then((res) => {
        mergeEntityPermissions('loadBalancers', res.data, 'Merged listener detail permissions')
        setListener(res.data)
        setPageLoading(false)
      })
      .catch((e) => {
        if(e?.response?.data?.detail){
          viewAlert({
            severity:'error',
            message:e?.response?.data?.detail
          })
        }
        // navigate('/not-found')
      })
    }
  }
  useEffect(() => {
    updateLb()
    const interval = setInterval(() => {
      updateLb()
    },10000)
    return () => {
      clearInterval(interval)
    }
  },[])
  const tabs = [
    {
      title:t("OVERVIEW"),
      slug:'overview',
      id:'1',
      visible: canViewOverview,
    },
    {
      title:t("POOLS"),
      slug:'pools',
      id:'3',
      visible: canViewPools,
    },
    {
      title:t("L7_POLICIES"),
      slug:'policies',
      id:'2',
      visible: canViewPolicies,
    },
  ]
  const visibleTabs = tabs.filter((tab) => tab.visible)
  const defaultTab = visibleTabs[0]?.slug
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<string>()
  useEffect(() => {
    visibleTabs.map((item:any) => {
      if(location.pathname.includes(item.slug)){
        setActiveTab(item.slug)
      }
    })
  },[location, visibleTabs])
  const optionsRef:any = useRef()
  const dropsRef:any = useRef()
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  },[]);
  const handleClickOutside = (event:any) => {
    if (optionsRef?.current && dropsRef?.current && !optionsRef.current.contains(event.target) && !dropsRef.current.contains(event.target)){
      setOptions(false)
    }
  }

  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const closeModal = (values:any) => {
    if(modalType === 'delete'){
      if(!lbId) return;
      setLoading(true);
      loadBalancerService.deleteListener(lbId, listener.id)
      .then((res) => {
        updateLb()
        setViewModal(false)
        setActiveAnimation(false)
        navigate(-1)
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
    } else {
      setViewModal(false)
      setActiveAnimation(false)
    }
  }

  const items = [
    {
      title:t("EDIT"),
      id:'2',
      icon:icons.Edit,
      permissionSurface: permissionSurfaces.detailActions.listenerEdit,
      onClick: () => navigate(`/load-balancer-listener/${lbId}/${listener.id}/edit`),
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      permissionSurface: permissionSurfaces.detailActions.listenerDelete,
      onClick:() => {
        setAlertTitle(t("LISTENER"))
        openModal('delete')
        setAlertSubTitle([listener])
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  const visibleItems = usePermissionActionFilter(items, { itemAllowedActions: listener?.allowed_actions })
  if (!defaultTab) return <Navigate replace to="/403" />
  if(pageLoading) return <TopProgress loading={pageLoading}/>
  return(
    <Container>
      <BackArrow onClick={() => navigate(`/load-balancers/${lbId}/listeners`)} title={t("GO_BACK")}/>
      <HeadWrapper>
        <InstanceName>
          {listener?.name || listener.id} 
        </InstanceName>
        <RightHeadContainer>
          {visibleItems.length > 0 ?
          <>
            <div ref={dropsRef}>
              <Button
                variant="stroke"
                size='small'
                icon={icons.More}
                active={options}
                title={`${t("MENU")}`}
                onClick={() => setOptions(!options)}
              />
            </div>
            <PropsContainer style={{top:'35px', right:'0px'}} ref={optionsRef} active={options}>
              <DropOptions
                options={items}
                itemToEdit={listener}
              />
            </PropsContainer>
          </>
          : null}
        </RightHeadContainer>
      </HeadWrapper>
      <InstanceHeadInfo>
        <StateWrapper>
        <GreenDot active={listener.operating_status}/>
          <Greentext active={listener.operating_status}>
          {handleStatusName(listener.operating_status, t)}
          </Greentext>
        </StateWrapper>
      </InstanceHeadInfo>
      <NavWrapper>
        <div>
          <Tabs
            tabs={visibleTabs}
            size='display'
            location={activeTab}
            onChange={setActiveTab}
          />
        </div>
      </NavWrapper>
      <Routes>
        <Route path="/" element={<Navigate replace to={defaultTab} />} /> 
        {canViewOverview && <Route path='/overview' element={<Overview lb={listener}/>}/>} 
        {canViewPolicies && <Route path='/policies' element={<Policies/>}/>} 
        {/* <Route path='/listeners/create' element={<CreateLoadBalancer/>}/> */}
        {canViewPools && <Route path='/pools' element={<Pools/>}/>} 
        {/* <Route path='/pools/create' element={<CreateLoadBalancer/>}/> */}
        <Route path='*' element={<Navigate replace to={defaultTab} />} />
      </Routes>
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        // dataToEdit={instance}
        instanceId={listener.id}
        // instance={instance}
        loading={loading}
      />
    </Container>
  )
}

export default LbListeners
